/*
 * pdp-runtime.js — self-hosted runtime
 * for the migrated content sections. Zero third-party dependency / its app embed.
 * Reproduces the only three behaviours those sections use:
 *   1. scroll reveals  — remove `.pdp-scroll-trigger--hidden` when in view
 *   2. FAQ accordion   — click header → toggle [data-pdp-type=accordion-item]
 *   3. ticker (marquee)— seamless CSS marquee driven by data-pdp-ticker-speed
 * Re-inits on Shopify's `shopify:section:load` so it works in the theme editor.
 */
(function () {
  'use strict';

  // ------------------------------------------------------------- 1) reveals
  var io = null;
  function reveal(el) {
    el.classList.remove('pdp-scroll-trigger--hidden');
  }
  function initReveals(root) {
    var els = (root || document).querySelectorAll(
      '.pdp-scroll-trigger.pdp-scroll-trigger--hidden'
    );
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(reveal);
      return;
    }
    if (!io) {
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            var replay = e.target.getAttribute('data-pdp-scroll-into-view-replay') === 'true';
            if (e.isIntersecting) {
              reveal(e.target);
              if (!replay) io.unobserve(e.target);
            } else if (replay) {
              e.target.classList.add('pdp-scroll-trigger--hidden');
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.04 }
      );
    }
    els.forEach(function (el) { io.observe(el); });
  }

  // --------------------------------------------------------- 2) accordions
  function closeAccordionItem(item) {
    if (!item || item.getAttribute('data-state') !== 'open') return;
    item.setAttribute('data-state', 'closed');
    var itemHeader = item.querySelector('[data-pdp-type="accordion-header"]');
    if (itemHeader) itemHeader.setAttribute('aria-expanded', 'false');
    var itemContent = item.querySelector('[data-pdp-type="accordion-content"]');
    if (itemContent) itemContent.style.setProperty('--pdp-accordion-content-height', '0px');
  }

  function initAccordions(root) {
    (root || document)
      .querySelectorAll('[data-pdp-type="accordion-header"]')
      .forEach(function (header) {
        if (header.__lvAcc) return;
        header.__lvAcc = true;
        header.style.cursor = 'pointer';
        var boundItem = header.closest('[data-pdp-type="accordion-item"]');
        if (boundItem && !header.hasAttribute('aria-expanded')) {
          header.setAttribute('aria-expanded', boundItem.getAttribute('data-state') === 'open' ? 'true' : 'false');
        }
        if (boundItem && boundItem.getAttribute('data-state') === 'open') {
          var openContent = boundItem.querySelector('[data-pdp-type="accordion-content"]');
          if (openContent) {
            openContent.style.setProperty('--pdp-accordion-content-height', openContent.scrollHeight + 'px');
          }
        }
        header.addEventListener('click', function () {
          var item = header.closest('[data-pdp-type="accordion-item"]');
          if (!item) return;
          var content = item.querySelector('[data-pdp-type="accordion-content"]');
          var isOpen = item.getAttribute('data-state') === 'open';
          if (!isOpen) {
            var container = item.closest('[data-pdp-type="accordion-container"]');
            if (container) {
              container
                .querySelectorAll('[data-pdp-type="accordion-item"][data-state="open"]')
                .forEach(function (other) {
                  if (other !== item) closeAccordionItem(other);
                });
            }
          }
          item.setAttribute('data-state', isOpen ? 'closed' : 'open');
          header.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          if (content) {
            content.style.setProperty(
              '--pdp-accordion-content-height',
              isOpen ? '0px' : content.scrollHeight + 'px'
            );
          }
        });
      });
  }

  // ------------------------------------------------------------- 3) ticker
  var marqueeInjected = false;
  function initTickers(root) {
    (root || document)
      .querySelectorAll('.pdp-ticker-container')
      .forEach(function (container) {
        if (container.__lvTicker) return;
        container.__lvTicker = true;
        var ticker = container.querySelector('.pdp-ticker');
        var unit = ticker && ticker.querySelector('.pdp-ticker-initial-child-container');
        if (!ticker || !unit) return;

        if (!marqueeInjected) {
          var st = document.createElement('style');
          st.textContent = '@keyframes lvMarquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}';
          document.head.appendChild(st);
          marqueeInjected = true;
        }

        // Duplicate the content once → row is 2× wide → translateX(-50%) loops seamlessly.
        ticker.appendChild(unit.cloneNode(true));
        ticker.style.display = 'flex';
        ticker.style.flexWrap = 'nowrap';
        ticker.style.willChange = 'transform';

        var speed = parseFloat(container.getAttribute('data-pdp-ticker-speed') || '50') || 50;
        var reversed = container.getAttribute('data-pdp-ticker-is-reversed') === 'true';
        var pauseHover = container.getAttribute('data-pdp-ticker-pause') === 'true';

        var dur = Math.max(5, Math.round(unit.scrollWidth / speed));
        ticker.style.animation = 'lvMarquee ' + dur + 's linear infinite';
        if (reversed) ticker.style.animationDirection = 'reverse';
        if (pauseHover) {
          container.addEventListener('mouseenter', function () { ticker.style.animationPlayState = 'paused'; });
          container.addEventListener('mouseleave', function () { ticker.style.animationPlayState = 'running'; });
        }
      });
  }

  // ---------------------------------------------------------------- init
  function init(root) {
    initReveals(root);
    initAccordions(root);
    initTickers(root);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  // theme editor: re-init a freshly rendered section
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
